import {
  FieldError,
  Input,
  Label,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';

import styles from './VitalField.module.css';

/**
 * How a typed value compares with the clinical lead's rules (DESIGN.md §7, inputs):
 * - `normal`: nothing shown.
 * - `low` / `high`: out of range; the value turns bold with "Low" or "High". Passive.
 * - `critical_low` / `critical_high`: in the critical range; shown in danger red, the one
 *   colour reserved for clinical danger. Saving asks for an acknowledgement elsewhere.
 * - `no_range`: "No range set", so a blank note is never mistaken for "normal".
 */
export type VitalFlag = 'normal' | 'low' | 'high' | 'critical_low' | 'critical_high' | 'no_range';

export const VITAL_FLAG_WORDS: Record<VitalFlag, string | null> = {
  normal: null,
  low: 'Low',
  high: 'High',
  critical_low: 'Critical low',
  critical_high: 'Critical high',
  no_range: 'No range set',
};

export interface VitalFieldProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className' | 'style' | 'inputMode'
> {
  label: string;
  /** Inside the right edge: "°C", "mmHg", "kg". */
  unit: string;
  /** The judgement of the current value; absent while empty or not yet checkable. */
  flag?: VitalFlag;
  /** The rule behind the flag is not approved by a clinical lead yet. */
  provisional?: boolean;
  /** Why the value can't be saved, e.g. "Temperature 73 °C is not possible. Check the value." */
  errorMessage?: string;
  description?: string;
}

/**
 * A number input for one vital sign. Values are flagged, never blocked, when out of
 * range; only a physically impossible value is an error (CLAUDE.md clinical safety rules).
 */
export function VitalField({
  label,
  unit,
  flag,
  provisional = false,
  errorMessage,
  description,
  isInvalid,
  ...rest
}: VitalFieldProps) {
  const word = flag === undefined ? null : VITAL_FLAG_WORDS[flag];
  const abnormal = flag !== undefined && flag !== 'normal' && flag !== 'no_range';
  const critical = flag === 'critical_low' || flag === 'critical_high';
  return (
    <AriaTextField
      {...rest}
      inputMode="decimal"
      isInvalid={isInvalid === true || errorMessage !== undefined}
      className={styles.field}
      data-abnormal={abnormal || undefined}
      data-critical={critical || undefined}
    >
      <Label className={styles.label}>
        <span aria-hidden="true">{label}</span>
        <span className={styles.visuallyHidden}>{`${label} (${unit})`}</span>
      </Label>
      <div className={styles.row}>
        <div className={styles.control}>
          <Input className={styles.input} autoComplete="off" />
          <span className={styles.unit} aria-hidden="true">
            {unit}
          </span>
        </div>
        {word === null ? null : (
          <span className={styles.flag} data-flag={flag} role="status">
            {word}
            {provisional ? <span className={styles.provisional}> (provisional)</span> : null}
          </span>
        )}
      </div>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
      <FieldError className={styles.error}>{errorMessage}</FieldError>
    </AriaTextField>
  );
}
